
import { Button, Pagination, Table, Flex, Select, Space, Tag, Input, DatePicker, Switch, Avatar, Typography, Checkbox, message, Radio } from "antd";
import { useDebouncedCallback } from "use-debounce";
import dayjs from "dayjs";
import { RichText } from "@chatui/core";
import { useEffect, useState } from "react";
import { fetchEarningsEvents, resendEventsNotification, updateEarningsEvents } from "../../api/ai";
import { ArrowDownOutlined, ArrowUpOutlined, EyeFilled } from "@ant-design/icons";
import EventsContentReview from "./events_content_review";
import { conversionUtcDate, formatWithDecimal } from "../../utils/comm";
import { countryTuples } from 'country-region-data';

const EarningsEventView = () => {
    const countries = countryTuples.map(item => {
        return {
            label: item[0].split(',')[0],
            value: item[0],
            flag: item[1]
        }
    })
    const tabCols = [
        {
            title: 'Index',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text, _, index) => {
                return (pageNum - 1) * pageSize + (index + 1);
            },
        },
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'company',
            render: (text, _) => {
                return <strong>{text}</strong>;
            },
        },
        {
            title: 'Ticker',
            dataIndex: 'ticker',
            key: 'ticker',
            render: (text, _) => {
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            render: (text, _) => {
                return <Tag>{text}</Tag>;
            },
        },
        {
            title: 'EPS Forecast',
            dataIndex: 'epsForecast',
            key: 'epsForecast',
            render: (text, _) => {
                if (!text) return '';
                return formatWithDecimal(text);
            },
        },
        {
            title: 'Revenue Forecast',
            dataIndex: 'revenueForecast',
            key: 'revenueForecast',
            render: (text, _) => {
                if (!text) return '';
                return formatWithDecimal(text);
            },
        },
        {
            title: 'EPS Actual',
            dataIndex: 'epsActual',
            key: 'epsActual',
            render: (text, record) => {
                if (!text) return '';
                let isUp = parseFloat(text) > parseFloat(record.epsForecast);
                return <Space size={[6, 0]}>
                    <span>{formatWithDecimal(text)}</span>
                    {
                        isUp ? <ArrowUpOutlined style={{ color: 'green', fontSize: 12 }} /> : <ArrowDownOutlined style={{ color: 'red', fontSize: 12 }} />
                    }
                </Space>
            },
        },
        {
            title: 'Revenue Actual',
            dataIndex: 'revenueActual',
            key: 'revenueActual',
            render: (text, record) => {
                if (!text) return '';
                let isUp = parseFloat(text) > parseFloat(record.revenueForecast);
                return <Space size={[6, 0]}>
                    <span>{formatWithDecimal(text)}</span>
                    {
                        isUp ? <ArrowUpOutlined style={{ color: 'green', fontSize: 12 }} /> : <ArrowDownOutlined style={{ color: 'red', fontSize: 12 }} />
                    }
                </Space>
            },
        },
        {
            title: 'Release Date',
            dataIndex: 'releaseDate',
            key: 'releaseDate',
            render: (text, _) => {
                return <label style={{ fontSize: 12, color: '#afafaf', whiteSpace: 'nowrap' }}>{dayjs(text).format('MM-DD HH:mm')}</label>
            },
        },
        {
            title: 'Summary',
            dataIndex: 'summary',
            key: 'summary',
            render: (_, record) => {
                try {
                    let summary = record.summary;
                    if (!summary) return '';
                    summary = JSON.parse(summary);
                    if (!summary.article_body) return '';
                    return (
                        <Space>
                            <div style={{ background: '#f1f1f1', padding: '8px 12px', borderRadius: 8, width: 160 }}>
                                <div className="community-content">
                                    <RichText content={summary.article_body} />
                                </div>
                            </div>
                            <EyeFilled style={{ color: 'grey', cursor: 'pointer' }} onClick={() => reviewHandle(record, 1)} />
                        </Space>
                    )
                } catch (error) {
                    return '';
                }
            },
        },
        {
            title: 'Notification',
            dataIndex: 'notification',
            key: 'notification',
            render: (_, record) => {
                try {
                    let summary = record.summary;
                    if (!summary) return '';
                    summary = JSON.parse(summary);
                    if (!summary.notification_body) return '';
                    return (
                        <Space>
                            <div style={{ background: '#f1f1f1', padding: '8px 12px', borderRadius: 8, width: 160 }}>
                                <div className="community-content">
                                    <RichText content={summary.notification_body} />
                                </div>
                            </div>
                            <EyeFilled style={{ color: 'grey', cursor: 'pointer' }} onClick={() => reviewHandle(record, 2)} />
                        </Space>
                    )
                } catch (error) {
                    return '';
                }
            },
        },
        {
            title: 'Schedule Report',
            dataIndex: 'scheduleReport',
            key: 'secheduleReport',
            fixed: 'right',
            render: (text, record) => {
                let disabled = record.isReleased;
                let schedulePreReport = record.schedulePreReport;
                let schedulePostReport = record.schedulePostReport;
                return (
                    <>
                        <Checkbox disabled={disabled} checked={schedulePreReport == 1} onChange={(e) => updateReportDisabled(record, e.target.checked, 'schedulePreReport')}>Pre</Checkbox>
                        <Checkbox disabled={disabled} checked={schedulePostReport == 1} onChange={(e) => updateReportDisabled(record, e.target.checked, 'schedulePostReport')}>Post</Checkbox>
                    </>
                )
            },
        },
        {
            title: 'Report Status',
            dataIndex: 'reportStatus',
            key: 'reportStatus',
            fixed: 'right',
            render: (_, record) => {
                const { schedulePreReport, schedulePostReport, schedulePreReportStatus, schedulePostReportStatus } = record;
                if (schedulePreReport === 0 && schedulePostReport === 0) return '';
                // schedulePreReportStatus 0:-- 2:generated 1:scheduled
                // schedulePostReportStatus 0:-- 2:generated 1:scheduled
                let preStatus = schedulePreReportStatus === 2 ? 'Generated' : schedulePreReportStatus === 1 ? 'Scheduled' : '--';
                let postStatus = schedulePostReportStatus === 2 ? 'Generated' : schedulePostReportStatus === 1 ? 'Scheduled' : '--';
                return <Space wrap size={[2, 2]}>
                    {schedulePreReport === 1 && <Tag style={{ fontSize: 12 }}>Pre: {preStatus}</Tag>}
                    {schedulePostReport === 1 && <Tag style={{ fontSize: 12 }}>Post: {postStatus}</Tag>}
                </Space>
            },
        },
        {
            title: 'Schedule Notification User',
            dataIndex: 'scheduleNotification',
            key: 'scheduleNotification',
            fixed: 'right',
            render: (text, record) => {
                const { schedulePreReport, schedulePostReport } = record;
                const disabledWhenReleased = record.isReleased;
                const preDisabledWhenSent = record.schedulePreNotificationStatus >= 2;
                const postDisabledWhenSent = record.schedulePostNotificationStatus >= 2;
                return <Flex vertical>
                    {
                        schedulePreReport === 1 && (
                            <Space size={[2, 2]}>
                                <strong>Pre:&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                                <Radio.Group block disabled={disabledWhenReleased || preDisabledWhenSent} value={record.schedulePreNotification} onChange={(e) => updateReportDisabled(record, e.target.value, 'schedulePreNotification')}>
                                    <Radio value={0}>Interested</Radio>
                                    <Radio value={1}>All</Radio>
                                </Radio.Group>
                            </Space>
                        )
                    }
                    {
                        schedulePostReport === 1 && (
                            <Space size={[2, 2]}>
                                <strong>Post:&nbsp;&nbsp;&nbsp;&nbsp;</strong>
                                <Radio.Group block disabled={disabledWhenReleased || postDisabledWhenSent} value={record.schedulePostNotification} onChange={(e) => updateReportDisabled(record, e.target.value, 'schedulePostNotification')}>
                                    <Radio value={0}>Interested</Radio>
                                    <Radio value={1}>All</Radio>
                                </Radio.Group>
                            </Space>
                        )
                    }
                </Flex>
            },
        },
        {
            title: 'Notification Status',
            dataIndex: 'notificationStatus',
            key: 'notificationStatus',
            fixed: 'right',
            render: (text, record) => {
                const { schedulePostNotificationStatus, schedulePreNotificationStatus, schedulePreReport, schedulePostReport } = record;
                // schedulePreNotificationStatus 0:-- 1:scheduled 2:sent preview 3:sent reminder
                // schedulePostNotificationStatus 0:-- 1:scheduled 2:sent
                let preStatus = schedulePreNotificationStatus === 1 ? 'Scheduled' : schedulePreNotificationStatus === 2 ? 'Sent Preview' : schedulePreNotificationStatus === 3 ? 'Sent Reminder' : '--';
                let postStatus = schedulePostNotificationStatus === 1 ? 'Scheduled' : schedulePostNotificationStatus === 2 ? 'Sent' : '--';
                return (
                    <Space wrap size={[2, 2]}>
                        {schedulePreReport === 1 && <Tag style={{ fontSize: 12 }}>Pre: {preStatus}</Tag>}
                        {schedulePostReport === 1 && <Tag style={{ fontSize: 12 }}>Post: {postStatus}</Tag>}
                    </Space>
                )
            },
        },
        {
            title: 'ShowAt Home',
            dataIndex: 'isShowAtHome',
            key: 'isShowAtHome',
            fixed: 'right',
            render: (text, record) => {
                return <Checkbox checked={text} onChange={(e) => updateShowAtHome(record, e.target.checked)} />
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            fixed: 'right',
            render: (text, record, index) => {
                if (!record.isShowAtHome) return '';
                return (
                    <Space size='small'>
                        <Button type="link" style={{ padding: 0, fontSize: 12 }} onClick={() => retryHandle(record)}>Analysis Event Retry</Button>
                    </Space>
                )
            },
        },
    ];

    const [searchParams, setSearchParams] = useState({
        eventStatus: undefined,
        notificationStatus: undefined,
        releaseDate: dayjs().format('YYYY-MM-DD'),
        country: undefined,
        ticker: undefined,
        isShowAtHome: undefined,
    });
    const [tabData, setTabData] = useState([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currEvents, setCurrEvents] = useState();
    const [previewType, setPreviewType] = useState();
    const [previewShow, setPreviewShow] = useState(false);

    useEffect(() => {
        fetchData(pageNum, pageSize, searchParams);
    }, [])


    const fetchData = useDebouncedCallback((page, pagesize, search) => {
        setLoading(true);
        let params = {
            'page': page,
            'pageSize': pagesize,
            ...search,
        };
        fetchEarningsEvents(params).then(res => {
            if (res.pagination) {
                setTotal(res.pagination.totalCount);
            }
            if (res.events) {
                setTabData(res.events);
            }
        }).finally(() => setLoading(false))
    }, 300)

    const pageChange = (page, size) => {
        setPageNum(page);
        setPageSize(size);
        fetchData(page, size, searchParams);
    }

    const onSearchChange = (tag, value) => {
        let newParams = { ...searchParams };
        newParams[tag] = value;
        setSearchParams(newParams);
        setPageNum(1);
        fetchData(1, pageSize, newParams);
    }



    const updateReportDisabled = (record, value, type) => {
        let params = {
            eventId: record.id,
            [type]: value ? 1 : 0,
        }
        record[type] = value ? 1 : 0;
        setTabData([...tabData]);

        setLoading(true);
        updateEarningsEvents(params).then(res => {
            if (!res) {
                message.error('Failed to update report disabled');
            }
        }).finally(() => {
            fetchData(pageNum, pageSize, searchParams);
        });
    }

    const updateShowAtHome = (record, value) => {
        let params = {
            eventId: record.id,
            isShowAtHome: value,
        }
        record.isShowAtHome = value;
        setTabData([...tabData]);
        setLoading(true);
        updateEarningsEvents(params).then(res => {
            if (!res) {
                message.error('Failed to update show at home');
            }
        }).finally(() => {
            fetchData(pageNum, pageSize, searchParams);
        })
    }

    const reviewHandle = (record, type) => {
        setCurrEvents(record);
        setPreviewType(type);
        setPreviewShow(true);
    }

    const resendHandle = (record) => {
        let params = {
            earningsEventId: record.id
        };
        setLoading(true);
        resendEventsNotification(params).then(res => {
            if (res) {
                fetchData(pageNum, pageSize, searchParams);
            } else {
                setLoading(false);
            }
        });
    }

    const retryHandle = (record) => {
        let params = {
            eventId: record.id,
            isShowAtHomeRetryQuery: true,
        }

        setLoading(true);
        updateEarningsEvents(params).then(res => {
            if (res) {
                message.success('Retry success!');
            }
        }).finally(() => setLoading(false));
    }

    return (
        <div>
            <div className='top-part cnt-item'>
                <Flex justify="space-between">
                    <Space wrap style={{ justifyContent: 'flex-end' }}>
                        <Select
                            style={{ width: 160 }}
                            options={[
                                { key: 1, label: 'Pending', value: '0' },
                                { key: 2, label: 'Sent', value: '1' },
                            ]}
                            placeholder='Event status'
                            onChange={value => onSearchChange('eventStatus', value)}
                            allowClear
                        />
                        <Select
                            style={{ width: 160 }}
                            options={[
                                { key: 1, label: 'YES', value: true },
                                { key: 2, label: 'NO', value: false },
                            ]}
                            placeholder='Show At Home'
                            onChange={value => onSearchChange('isShowAtHome', value)}
                            allowClear
                        />
                        <Select
                            style={{ width: 220 }}
                            placeholder="country/city select..."
                            options={countries}
                            optionRender={(option) => {
                                let flag = `http://purecatamphetamine.github.io/country-flag-icons/3x2/${option.data.flag}.svg`;
                                return (
                                    <Space size={[4, 0]}>
                                        <Avatar src={flag} size={16} />
                                        <Typography.Text style={{ fontSize: 12 }}>
                                            <b>{option.data.label}</b>
                                        </Typography.Text>
                                    </Space>
                                )
                            }}
                            onChange={(val, option) => {
                                if (option) {
                                    onSearchChange('country', option.flag)
                                } else {
                                    onSearchChange('country', undefined)
                                }
                            }}
                            allowClear
                            showSearch />

                        <DatePicker
                            defaultValue={searchParams.releaseDate ? dayjs(searchParams.releaseDate) : undefined}
                            format={(value) => `${value ? conversionUtcDate(value).split(' ')[0] : ''}`}
                            placeholder="Release Date"
                            onChange={(_, date) => onSearchChange('releaseDate', date)}
                        />
                        <Input
                            placeholder="ticker search"
                            allowClear
                            onChange={evt => onSearchChange('ticker', evt.target.value)}
                        />
                    </Space>
                </Flex>
            </div>
            <div className='cnt-item'>
                <Table
                    scroll={{ x: true }}
                    loading={loading}
                    rowKey={(record) => record.id}
                    rowClassName={'table-row'}
                    size='small'
                    dataSource={tabData}
                    columns={tabCols}
                    pagination={false}
                />
            </div>
            <div className='footer-part cnt-item'>
                <Pagination
                    total={total}
                    showTotal={(total) => `total ${total}`}
                    current={pageNum}
                    pageSize={pageSize}
                    showSizeChanger={true}
                    pageSizeOptions={[10, 20, 30, 50]}
                    onChange={(page, size) => pageChange(page, size)}
                />
            </div>

            <EventsContentReview
                events={currEvents}
                open={previewShow}
                type={previewType}
                onClose={() => setPreviewShow(false)}
            />
        </div>
    )
}

export default EarningsEventView;